import React, { useEffect, useState } from 'react';
import {
  Award,
  CheckCircle2,
  Clock,
  Eye,
  FastForward,
  Pause,
  Play,
  Rewind,
  Sparkles,
  Trophy,
  X,
} from 'lucide-react';
import {
  FORMAT_PRESETS,
  type ClientGameView,
  type DeckStrengthSummary,
  type FormatId,
  type FormatLeaderboardEntry,
  type GameIntent,
  type PublicCardView,
} from '#shared';
import { playtableApi, type AccountProfileDTO } from '../lib/clientApi.ts';
import { PlayerBoard } from './PlayerBoard.tsx';

/**
 * Renders chat text with inline `[[Card Name]]` links that inspect the card when clicked.
 */
export function RichChatMessageText({
  text,
  onClickCardName,
}: {
  text: string;
  onClickCardName: (cardName: string) => void;
}) {
  const parts = text.split(/(\[\[[^\]]+\]\])/g);
  return (
    <>
      {parts.map((part, idx) => {
        if (part.startsWith('[[') && part.endsWith(']]')) {
          const cardName = part.slice(2, -2).trim();
          return (
            <button
              key={idx}
              type="button"
              onClick={() => onClickCardName(cardName)}
              className="mx-0.5 px-1.5 py-0.2 rounded bg-amber-500/20 hover:bg-amber-500/35 border border-amber-400/50 text-amber-200 font-semibold underline decoration-dotted"
            >
              {cardName}
            </button>
          );
        }
        return <span key={idx}>{part}</span>;
      })}
    </>
  );
}

export function PlayNowQueuePanel({
  ensureSignedIn,
  onMatchedPod,
  onToastError,
}: {
  ensureSignedIn: () => Promise<AccountProfileDTO | null>;
  onMatchedPod: (tableId: string) => void;
  onToastError: (msg: string) => void;
}) {
  const [format, setFormat] = useState<FormatId>('commander');
  const [mode, setMode] = useState<'unranked' | 'ranked'>('unranked');
  const [matchingStyle, setMatchingStyle] = useState<'random_fill' | 'balanced'>('random_fill');
  const [bracket, setBracket] = useState<1 | 2 | 3 | 4 | 5>(3);
  const [podChoice, setPodChoice] = useState<'4' | '3_4_5' | '2'>('4');
  const [queueState, setQueueState] = useState<{
    status: 'idle' | 'queued' | 'matched';
    waitedSec?: number;
    estimatedWaitSec?: number;
    matchedTableId?: string;
    matchedInviteCode?: string;
    queuePopulation: number;
  }>({ status: 'idle', queuePopulation: 0 });

  useEffect(() => {
    if (queueState.status !== 'queued') return;
    const timer = setInterval(async () => {
      try {
        const st = await playtableApi.getQueueStatus();
        setQueueState(st);
        if (st.status === 'matched' && st.matchedTableId) {
          onMatchedPod(st.matchedTableId);
        }
      } catch {
        // ignore transient poll error
      }
    }, 2500);
    return () => clearInterval(timer);
  }, [queueState.status, onMatchedPod]);

  const handleEnterQueue = async () => {
    try {
      const acct = await ensureSignedIn();
      if (!acct) return;
      const preferredPodSizes =
        podChoice === '2' ? [2] : podChoice === '3_4_5' ? [3, 4, 5] : [4];
      const res = await playtableApi.enqueueMatchmaking({
        format,
        mode,
        matchingStyle,
        preferredPodSizes,
        bracket,
        allowPublicBackfill: mode === 'unranked',
      });
      if (res.status === 'matched' && res.matchedTableId) {
        onMatchedPod(res.matchedTableId);
      } else {
        setQueueState({
          status: 'queued',
          waitedSec: 0,
          estimatedWaitSec: res.estimatedWaitSec,
          queuePopulation: 1,
        });
      }
    } catch (err: any) {
      onToastError(err.message);
    }
  };

  const handleLeaveQueue = async () => {
    await playtableApi.leaveQueue();
    setQueueState({ status: 'idle', queuePopulation: 0 });
  };

  return (
    <div className="rounded-2xl bg-gradient-to-br from-[#12192d] to-[#0b1020] border border-indigo-500/40 p-5 space-y-3 shadow-xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-400" />
          <h2 className="text-sm font-extrabold">Play Now Queue</h2>
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/20 border border-indigo-400/50 text-indigo-200 font-bold">
          60s Rule 0 Free Decline
        </span>
      </div>

      {queueState.status === 'queued' ? (
        <div className="p-3 rounded-xl bg-slate-950 border border-amber-500/40 space-y-2.5 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-bold text-amber-300 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 animate-spin" />
              Searching for {FORMAT_PRESETS[format]?.label} pod...
            </span>
            <span className="font-mono text-slate-300">
              {queueState.waitedSec ?? 0}s / ~{queueState.estimatedWaitSec ?? 25}s
            </span>
          </div>
          <p className="text-[11px] text-slate-400">
            {matchingStyle === 'balanced'
              ? 'Balanced Matching active: pairing by Bracket & Deck Strength (tolerance widens smoothly every 30s).'
              : 'Random Fill active: fastest available seat fill with mutual block-list protection.'}
          </p>
          <button
            type="button"
            onClick={handleLeaveQueue}
            className="w-full py-2 rounded-xl bg-rose-600/90 hover:bg-rose-500 text-white font-bold"
          >
            Leave Queue
          </button>
        </div>
      ) : (
        <div className="space-y-2.5 text-xs">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Format</label>
              <select
                value={format}
                onChange={(e) => {
                  const f = e.target.value as FormatId;
                  setFormat(f);
                  setPodChoice(FORMAT_PRESETS[f].defaultSeats === 2 ? '2' : '4');
                }}
                className="w-full px-2.5 py-1.5 rounded-xl bg-slate-950 border border-slate-800 text-white"
              >
                {Object.values(FORMAT_PRESETS).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Queue Mode</label>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value as 'unranked' | 'ranked')}
                className="w-full px-2.5 py-1.5 rounded-xl bg-slate-950 border border-slate-800 text-white"
              >
                <option value="unranked">Unranked (Casual / Backfill)</option>
                <option value="ranked">Ranked (90-Day Format Ladder)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Matching</label>
              <select
                value={matchingStyle}
                onChange={(e) => setMatchingStyle(e.target.value as 'random_fill' | 'balanced')}
                className="w-full px-2 py-1.5 rounded-xl bg-slate-950 border border-slate-800 text-white"
              >
                <option value="random_fill">Random Fill</option>
                <option value="balanced">Balanced (Opt-in)</option>
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Pod Size</label>
              <select
                value={podChoice}
                onChange={(e) => setPodChoice(e.target.value as '4' | '3_4_5' | '2')}
                className="w-full px-2 py-1.5 rounded-xl bg-slate-950 border border-slate-800 text-white"
              >
                <option value="4">4-Player Pod</option>
                <option value="3_4_5">Flex 3–5 Pod</option>
                <option value="2">1v1 (2 Seats)</option>
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">Bracket</label>
              <select
                value={bracket}
                onChange={(e) => setBracket(Number(e.target.value) as 1 | 2 | 3 | 4 | 5)}
                className="w-full px-2 py-1.5 rounded-xl bg-slate-950 border border-slate-800 text-white"
              >
                {[1, 2, 3, 4, 5].map((b) => (
                  <option key={b} value={b}>
                    Bracket B{b}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            type="button"
            onClick={handleEnterQueue}
            className="w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-950 font-extrabold text-xs shadow"
          >
            Find Pod Now
          </button>
        </div>
      )}
    </div>
  );
}

export function PostGameReportModal({
  view,
  onDispatch,
  onOpenReplay,
  onClose,
}: {
  view: ClientGameView;
  onDispatch: (intent: GameIntent) => void;
  onOpenReplay: () => void;
  onClose: () => void;
}) {
  const [winnerSeatIds, setWinnerSeatIds] = useState<string[]>(view.winnerSeatIds);
  const [isDraw, setIsDraw] = useState(false);
  const [elimTurns, setElimTurns] = useState<Record<string, number | null>>({});
  const [winTurn, setWinTurn] = useState<number>(view.turnNumber);
  const [lockedAt, setLockedAt] = useState<number | null>(null);
  const [strengths, setStrengths] = useState<Record<string, DeckStrengthSummary>>({});
  const [syncedProviders, setSyncedProviders] = useState<string[]>([]);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  const mySeat = view.viewerSeatId ? view.seats[view.viewerSeatId] : null;

  useEffect(() => {
    playtableApi
      .getMatchReportPrefill(view.tableId)
      .then((pre) => {
        setWinnerSeatIds(pre.winnerSeatIds);
        setIsDraw(pre.isDraw);
        setWinTurn(pre.turnNumber);
        if (pre.existingReport) setLockedAt(pre.existingReport.lockedAt);
        const turns: Record<string, number | null> = {};
        for (const s of pre.seats) {
          turns[s.seatId] = s.eliminatedTurn;
        }
        setElimTurns(turns);
      })
      .catch(() => {});
  }, [view.tableId]);

  const toggleWinner = (sid: string) => {
    setIsDraw(false);
    setWinnerSeatIds((prev) => (prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]));
  };

  const handleSubmit = async () => {
    try {
      const res = await playtableApi.submitMatchReport(view.tableId, {
        winnerSeatIds,
        isDraw,
        eliminationTurnsBySeat: elimTurns,
        winTurn,
      });
      setLockedAt(res.result.lockedAt);
      setStrengths(res.deckStrengths);
      setSyncedProviders(res.syncedProviders);
      setStatusMsg('Match outcome saved! Editable for 24 hours before locking.');
    } catch (err: any) {
      setStatusMsg(err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-[105] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="max-w-lg w-full rounded-2xl bg-slate-950 border border-slate-800 p-5 space-y-4 text-xs shadow-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-400" />
            <h2 className="text-sm font-extrabold text-white">Two-Tap Match Report & Post-Game</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-slate-800 text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-slate-400 leading-relaxed">
          Elimination turns are prefilled automatically from table history. Tap the winning seat(s) (or Draw) and confirm—reports stay editable for 24 hours.
        </p>

        <div className="space-y-1.5">
          {view.seatOrder.map((sid) => {
            const s = view.seats[sid];
            if (!s || !s.publicId) return null;
            const won = !isDraw && winnerSeatIds.includes(sid);
            return (
              <div
                key={sid}
                className={`p-2.5 rounded-xl border flex items-center justify-between gap-2 ${
                  won ? 'bg-amber-500/15 border-amber-400' : 'bg-slate-900 border-slate-800'
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleWinner(sid)}
                  className="flex items-center gap-2 text-left flex-1"
                >
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.profileColor }} />
                  <div>
                    <div className="font-bold text-white">
                      {s.displayName} {won ? '👑 WINNER' : ''}
                    </div>
                    <div className="text-[10px] text-slate-400">{s.deckMeta?.name ?? 'Deck'}</div>
                  </div>
                </button>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-slate-400">Elim T:</span>
                  <input
                    type="number"
                    min={1}
                    placeholder="—"
                    value={elimTurns[sid] ?? ''}
                    onChange={(e) =>
                      setElimTurns((prev) => ({
                        ...prev,
                        [sid]: e.target.value ? Number(e.target.value) : null,
                      }))
                    }
                    className="w-14 px-2 py-1 rounded bg-slate-950 border border-slate-700 text-center"
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isDraw}
              onChange={(e) => {
                setIsDraw(e.target.checked);
                if (e.target.checked) setWinnerSeatIds([]);
              }}
              className="accent-amber-500"
            />
            <span className="font-semibold text-slate-200">Report as Draw</span>
          </label>

          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">Ending Turn:</span>
            <input
              type="number"
              min={1}
              value={winTurn}
              onChange={(e) => setWinTurn(Math.max(1, Number(e.target.value) || 1))}
              className="w-16 px-2 py-1 rounded bg-slate-900 border border-slate-700 text-center"
            />
          </div>
        </div>

        {mySeat && (
          <label className="flex items-center justify-between p-2.5 rounded-xl bg-slate-900 border border-slate-800 cursor-pointer">
            <div>
              <div className="font-bold text-slate-100">Share My Hand in Post-Game & Replay</div>
              <div className="text-[10px] text-slate-400">
                Hidden information stays private forever unless you opt in here.
              </div>
            </div>
            <input
              type="checkbox"
              checked={mySeat.revealHandsPostGame}
              onChange={(e) => onDispatch({ type: 'TOGGLE_POSTGAME_HAND_REVEAL', enabled: e.target.checked })}
              className="accent-emerald-500"
            />
          </label>
        )}

        {Object.values(strengths).length > 0 && (
          <div className="p-2.5 rounded-xl bg-indigo-950/50 border border-indigo-500/40 space-y-1">
            <div className="text-[10px] font-bold uppercase text-indigo-300">Updated Deck Era Strength</div>
            {Object.values(strengths).map((ds) => (
              <div key={ds.deckId} className="flex justify-between text-[11px]">
                <span className="text-slate-300">Era {ds.eraNumber}</span>
                <span className="font-mono font-bold text-amber-300">{ds.badge}</span>
              </div>
            ))}
          </div>
        )}

        {syncedProviders.length > 0 && (
          <div className="text-[11px] text-emerald-300 flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>Synced to external trackers: {syncedProviders.join(', ')}</span>
          </div>
        )}

        {statusMsg && <div className="text-[11px] text-amber-300">{statusMsg}</div>}
        {lockedAt && (
          <div className="text-[10px] text-slate-500">
            Edit lock timestamp: {new Date(lockedAt).toLocaleString()}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 pt-1">
          <button
            type="button"
            onClick={onOpenReplay}
            className="py-2 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 font-bold text-slate-200 flex items-center justify-center gap-1.5"
          >
            <Eye className="w-3.5 h-3.5" />
            <span>Scrub Replay</span>
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold"
          >
            Confirm Match Report
          </button>
        </div>
      </div>
    </div>
  );
}

export function FormatLeaderboardModal({
  profile,
  onProfileChanged,
  onClose,
}: {
  profile: AccountProfileDTO | null;
  onProfileChanged: (p: AccountProfileDTO) => void;
  onClose: () => void;
}) {
  const [format, setFormat] = useState<FormatId>('commander');
  const [entries, setEntries] = useState<FormatLeaderboardEntry[]>([]);
  const [myStanding, setMyStanding] = useState<{
    qualified: boolean;
    games90d: number;
    minGamesRequired: number;
    distinctDecks90d: number;
    minDecksRequired: number;
    rating: number | null;
    winRatePct: number | null;
  } | null>(null);

  const refresh = async (fmt: FormatId) => {
    const res = await playtableApi.getFormatLeaderboard(fmt);
    setEntries(res.entries);
    setMyStanding(res.myStanding);
  };

  useEffect(() => {
    refresh(format).catch(() => {});
  }, [format]);

  return (
    <div className="fixed inset-0 z-[105] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="max-w-xl w-full rounded-2xl bg-slate-950 border border-slate-800 p-5 space-y-4 text-xs shadow-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-amber-400" />
            <h2 className="text-sm font-extrabold text-white">Per-Format Rolling 90-Day Rankings</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-slate-800 text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center justify-between gap-3">
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value as FormatId)}
            className="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-700 text-white font-bold"
          >
            {Object.values(FORMAT_PRESETS).map((p) => (
              <option key={p.id} value={p.id}>
                {p.label} Leaderboard
              </option>
            ))}
          </select>

          {profile && (
            <label className="flex items-center gap-2 cursor-pointer text-[11px] text-slate-300">
              <input
                type="checkbox"
                checked={Boolean(profile.leaderboardOptIn)}
                onChange={async (e) => {
                  const updated = await playtableApi.updateProfile({ leaderboardOptIn: e.target.checked });
                  onProfileChanged(updated.profile);
                  refresh(format);
                }}
                className="accent-amber-500"
              />
              <span>Opt in to public leaderboard</span>
            </label>
          )}
        </div>

        <div className="p-3 rounded-xl bg-slate-900/90 border border-slate-800 space-y-1 text-[11px]">
          <div className="font-bold text-slate-200">How Format Standings & Deck Strength Work</div>
          <p className="text-slate-400">
            Rankings are strictly per-format over a rolling 90-day window and require at least{' '}
            <strong className="text-white">10 tracked games</strong> across{' '}
            <strong className="text-white">
              {FORMAT_PRESETS[format]?.minDecksForRank ?? 3}+ distinct decks
            </strong>{' '}
            so no single tuned deck inflates a player rank. Deck Strength (`1.8K · T9 · B4 · 32%`) unlocks per Deck Era after 10 tracked games.
          </p>
          {myStanding && (
            <div className="pt-1 text-amber-300 font-semibold">
              {myStanding.qualified
                ? `Your ${FORMAT_PRESETS[format]?.label} Standing: ${myStanding.rating} Elo (${myStanding.winRatePct}% win · ${myStanding.games90d} games / ${myStanding.distinctDecks90d} decks)`
                : `Qualification Progress: ${myStanding.games90d}/${myStanding.minGamesRequired} games · ${myStanding.distinctDecks90d}/${myStanding.minDecksRequired} distinct decks in last 90d`}
            </div>
          )}
        </div>

        <div className="max-h-72 overflow-y-auto divide-y divide-slate-800 rounded-xl border border-slate-800 bg-slate-900/50">
          {entries.length === 0 ? (
            <div className="py-8 text-center text-slate-500">
              No opted-in players have completed the 90-day multi-deck qualification in {FORMAT_PRESETS[format]?.label} yet.
            </div>
          ) : (
            entries.map((entry) => (
              <div key={entry.publicId} className="px-3 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="font-mono font-extrabold text-amber-400 w-6">#{entry.rank}</span>
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.profileColor }} />
                  <span className="font-bold text-white">{entry.displayName}</span>
                </div>
                <div className="flex items-center gap-3 font-mono text-[11px]">
                  <span className="text-slate-400">
                    {entry.games90d}g · {entry.distinctDecks90d} decks · {entry.winRatePct}%
                  </span>
                  <span className="font-extrabold text-emerald-300">{entry.rating}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export function ReplayViewerModal({
  tableId,
  onInspectCard,
  onClose,
}: {
  tableId: string;
  onInspectCard: (c: PublicCardView) => void;
  onClose: () => void;
}) {
  const [step, setStep] = useState<number | undefined>(undefined);
  const [totalSteps, setTotalSteps] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [frame, setFrame] = useState<ClientGameView | null>(null);
  const [milestones, setMilestones] = useState<
    { step: number; seq: number; turnNumber: number; type: string; summary: string }[]
  >([]);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    playtableApi
      .getReplayFrame(tableId, step)
      .then((res) => {
        if (cancelled) return;
        setTotalSteps(res.totalSteps);
        setCurrentStep(res.currentStep);
        setFrame(res.frame);
        setMilestones(res.milestones);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tableId, step]);

  useEffect(() => {
    if (!playing) return;
    const t = setInterval(() => {
      setStep((prev) => {
        const cur = prev ?? currentStep;
        if (cur >= totalSteps) {
          setPlaying(false);
          return totalSteps;
        }
        return cur + 1;
      });
    }, 900);
    return () => clearInterval(t);
  }, [playing, currentStep, totalSteps]);

  return (
    <div className="fixed inset-0 z-[105] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3">
      <div className="max-w-5xl w-full max-h-[92vh] rounded-2xl bg-slate-950 border border-slate-800 p-4 flex flex-col gap-3 text-xs shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-extrabold text-white">
              Scrubbable Match Replay · Step {currentStep} / {totalSteps}
            </div>
            <div className="text-[10px] text-slate-400">
              Unrevealed libraries, face-down cards, and opponents’ hands stay strictly private unless a player opted in post-game.
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-300">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrubber Controls */}
        <div className="flex items-center gap-2 bg-slate-900 p-2.5 rounded-xl border border-slate-800">
          <button
            type="button"
            onClick={() => setStep(Math.max(0, currentStep - 1))}
            className="p-1.5 rounded bg-slate-800 hover:bg-slate-700"
          >
            <Rewind className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setPlaying((p) => !p)}
            className="px-3 py-1.5 rounded-lg bg-amber-500 text-slate-950 font-extrabold flex items-center gap-1"
          >
            {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            <span>{playing ? 'Pause' : 'Play'}</span>
          </button>
          <button
            type="button"
            onClick={() => setStep(Math.min(totalSteps, currentStep + 1))}
            className="p-1.5 rounded bg-slate-800 hover:bg-slate-700"
          >
            <FastForward className="w-3.5 h-3.5" />
          </button>

          <input
            type="range"
            min={0}
            max={Math.max(1, totalSteps)}
            value={currentStep}
            onChange={(e) => {
              setPlaying(false);
              setStep(Number(e.target.value));
            }}
            className="flex-1 accent-amber-500"
          />

          <span className="font-mono text-amber-300">
            Turn {frame?.turnNumber ?? 1}
          </span>
        </div>

        {frame && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-3 flex-1 overflow-y-auto min-h-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 overflow-y-auto">
              {frame.seatOrder.map((sid) => {
                const s = frame.seats[sid];
                if (!s || !s.publicId) return null;
                return (
                  <div key={sid} className="space-y-1">
                    <PlayerBoard
                      seat={s}
                      isMe={sid === frame.viewerSeatId}
                      isActiveTurn={frame.activeSeatId === sid}
                      useCommandZone={frame.settings.useCommandZone}
                      compact
                      reducedMotion
                      onDispatch={() => {}}
                      onInspectCard={onInspectCard}
                      onOpenCardMenu={onInspectCard}
                      onOpenZoneBrowser={() => {}}
                    />
                    <div className="px-2 py-1 rounded bg-slate-900 border border-slate-800 text-[10px] text-slate-400 flex justify-between">
                      <span>
                        Hand ({s.zones.handCount}):{' '}
                        {s.zones.handCards
                          ? s.zones.handCards.map((c) => c.card?.name).join(', ') || 'Empty'
                          : 'Hidden (not opted in)'}
                      </span>
                      {s.revealHandsPostGame && (
                        <span className="text-emerald-400 font-bold">Opted In</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="rounded-xl bg-slate-900/90 border border-slate-800 p-2 overflow-y-auto space-y-1 max-h-[60vh]">
              <div className="text-[10px] font-bold uppercase text-slate-400 sticky top-0 bg-slate-900 py-1">
                Event Timeline
              </div>
              {milestones.map((m) => (
                <button
                  key={m.seq}
                  type="button"
                  onClick={() => {
                    setPlaying(false);
                    setStep(m.step);
                  }}
                  className={`w-full text-left px-2 py-1 rounded text-[10px] ${
                    m.step === currentStep
                      ? 'bg-amber-500/20 border border-amber-400/60 text-amber-100 font-bold'
                      : 'hover:bg-slate-800 text-slate-300'
                  }`}
                >
                  <span className="font-mono text-amber-400 mr-1">T{m.turnNumber}</span>
                  <span>{m.summary}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
